// frontend/client/src/components/DashboardOverview.js
// OPTIMIZED: Command Center with functional Content Pipeline, brand scan, creation modal

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMinoStore } from '../store/useMinoStore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { api } from '../services/apiClient';
import { getContrastText } from '../utils/colorContrast';
import { useAnalytics } from '../hooks/useAnalytics';
import {
  TrendingUp, TrendingDown, Eye, Users, Heart, Target,
  Zap, Calendar, BarChart3, Plus, Sparkles,
  Instagram, Twitter, Linkedin, Youtube, MessageCircle,
  FileVideo, Wand2, X, Scan, ChevronRight, Loader2, Award,
  Upload, Scissors, MessageSquare, EyeIcon, Send, ArrowRight,
  CheckCircle2, Circle, ChevronDown
} from 'lucide-react';

const PLATFORM_CONFIG = {
  instagram: { name: 'Instagram', icon: Instagram, color: '#E1306C' },
  tiktok:    { name: 'TikTok',    icon: MessageCircle, color: '#000000' },
  twitter:   { name: 'Twitter/X', icon: Twitter,    color: '#000000' },
  linkedin:  { name: 'LinkedIn',  icon: Linkedin,   color: '#0A66C2' },
  youtube:   { name: 'YouTube',   icon: Youtube,    color: '#FF0000' },
};

const PLAN_CONFIG = {
  freemium:  { label: 'Free',       color: '#6B7280' },
  starter:   { label: 'Starter',    color: '#4F46E5' },
  creator:   { label: 'Creator',    color: '#4F46E5' },
  growth:    { label: 'Growth',     color: '#8B5CF6' },
  scale:     { label: 'Scale',      color: '#F59E0B' },
  business:  { label: 'Business',   color: '#F59E0B' },
  executive: { label: 'Executive',  color: '#10B981' },
};

const DEFAULT_SERVICE_STATUS = {
  instagram: { mode: 'live',        label: 'Live',        available: true },
  tiktok:    { mode: 'sandbox',     label: 'Sandbox',      available: true },
  twitter:   { mode: 'coming-soon', label: 'Coming Soon',  available: false },
  linkedin:  { mode: 'coming-soon', label: 'Coming Soon',  available: false },
  youtube:   { mode: 'coming-soon', label: 'Coming Soon',  available: false },
};

const CREATION_OPTIONS = [
  { id: 'video',   icon: FileVideo, color: '#4F46E5', label: 'Video Post',   desc: 'Upload, edit and publish a video',       path: '/dashboard/edit-studio' },
  { id: 'image',   icon: Eye,       color: '#8B5CF6', label: 'Image Post',   desc: 'Design or upload an image',               path: '/dashboard/media' },
  { id: 'caption', icon: Wand2,     color: '#10B981', label: 'Caption Only', desc: 'Generate AI copy for an existing asset',  path: '/dashboard/edit-studio' },
];

const QUICK_ACTIONS = [
  { label: 'Upload Video',    icon: FileVideo,  color: '#4F46E5', path: '/dashboard/edit-studio' },
  { label: 'View Analytics',  icon: BarChart3,  color: '#10B981', path: '/dashboard/analytics' },
  { label: 'Schedule Content',icon: Calendar,   color: '#F59E0B', path: '/dashboard/calendar' },
  { label: 'AI Coach',        icon: Sparkles,   color: '#EC4899', path: '/dashboard/analytics' },
];

const PIPELINE_STEPS = [
  { id: 'upload',   label: 'Upload',   icon: Upload,        path: '/dashboard/edit-studio', desc: 'Add your content' },
  { id: 'enhance',  label: 'Enhance',  icon: Scissors,      path: '/dashboard/edit-studio', desc: 'AI optimization' },
  { id: 'generate', label: 'Generate', icon: MessageSquare, path: '/dashboard/edit-studio', desc: 'Captions & hooks' },
  { id: 'preview',  label: 'Preview',  icon: EyeIcon,       path: '/dashboard/preview',     desc: 'Multi-platform' },
  { id: 'publish',  label: 'Publish',  icon: Send,          path: '/dashboard/calendar',    desc: 'Go live' },
];

const DO_CSS = `
  .do-root { display: flex; flex-direction: column; gap: 20px; padding: 24px; font-family: var(--font-body); }
  .do-section-title { font-family: var(--font-display); font-size: 15px; font-weight: 700; color: var(--text-main); margin: 0 0 14px; }
  .do-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 20px; box-shadow: var(--shadow-md); }
  .do-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .do-stat-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 18px 20px; box-shadow: var(--shadow-md); transition: border-color 0.2s, transform 0.2s; }
  .do-stat-card:hover { border-color: var(--brand-border); transform: translateY(-1px); }
  .do-stat-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin: 0 0 6px; }
  .do-stat-value { font-family: var(--font-display); font-size: 26px; font-weight: 700; color: var(--text-main); margin: 0 0 6px; letter-spacing: -0.02em; line-height: 1; }
  .do-delta { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 9999px; }
  .do-delta-up { background: #dcfce7; color: #16a34a; }
  .do-delta-down { background: #fee2e2; color: #dc2626; }
  .do-actions-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
  .do-action-btn { display: flex; align-items: center; gap: 10px; padding: 13px 14px; border-radius: 12px; border: 1px solid var(--border-light); background: var(--bg-surface); cursor: pointer; transition: all 0.2s; text-decoration: none; }
  .do-action-btn:hover { border-color: var(--brand-border); transform: translateY(-1px); box-shadow: var(--shadow-md); }
  .do-action-icon { width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .do-action-label { font-size: 12px; font-weight: 700; color: var(--text-main); }
  .do-platform-row { display: flex; align-items: center; gap: 12px; padding: 11px 0; border-bottom: 1px solid var(--border-light); }
  .do-platform-row:last-child { border-bottom: none; }
  .do-activity-row { display: flex; align-items: flex-start; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border-light); }
  .do-activity-row:last-child { border-bottom: none; }
  .do-activity-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; }
  .do-connect-btn { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 6px; background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); font-size: 11px; font-weight: 700; border: none; cursor: pointer; text-decoration: none; transition: all 0.15s; }
  .do-connect-btn:hover { background: var(--brand-primary); color: var(--brand-primary-contrast, white); }
  .do-skeleton { background: var(--border-light); border-radius: 8px; animation: do-pulse 1.5s ease-in-out infinite; }
  @keyframes do-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  .do-pipeline { display: flex; align-items: center; gap: 0; background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 20px 24px; box-shadow: var(--shadow-md); }
  .do-pipeline-step { display: flex; flex-direction: column; align-items: center; gap: 6px; flex: 1; position: relative; cursor: pointer; transition: all 0.2s; padding: 8px 4px; border-radius: 12px; }
  .do-pipeline-step:hover:not(.done):not(.current) { background: var(--bg-canvas); }
  .do-pipeline-step.current { transform: scale(1.08); }
  .do-pipeline-step.current .do-pipeline-icon-wrap { background: linear-gradient(135deg, #4F46E5, #8B5CF6); box-shadow: 0 4px 14px rgba(79,70,229,0.35); }
  .do-pipeline-step.current .do-pipeline-icon { color: white; }
  .do-pipeline-step.current .do-pipeline-label { color: #4F46E5; font-weight: 800; }
  .do-pipeline-step.done .do-pipeline-icon-wrap { background: #dcfce7; border-color: #86efac; }
  .do-pipeline-step.done .do-pipeline-icon { color: #16a34a; }
  .do-pipeline-step.done .do-pipeline-label { color: #16a34a; }
  .do-pipeline-step.muted .do-pipeline-icon-wrap { background: var(--bg-canvas); border-color: var(--border-light); }
  .do-pipeline-step.muted .do-pipeline-icon { color: var(--text-muted); }
  .do-pipeline-step.muted .do-pipeline-label { color: var(--text-muted); }
  .do-pipeline-icon-wrap { width: 40px; height: 40px; border-radius: 50%; border: 2px solid transparent; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
  .do-pipeline-label { font-size: 11px; font-weight: 700; color: var(--text-main); transition: all 0.2s; }
  .do-pipeline-desc { font-size: 10px; color: var(--text-muted); margin: 0; }
  .do-pipeline-connector { width: 24px; height: 2px; background: var(--border-light); flex-shrink: 0; margin-top: -14px; }
  .do-pipeline-connector.done { background: #86efac; }
  .do-pipeline-cta { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 6px; background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); font-size: 10px; font-weight: 700; margin-top: 2px; }
  .do-pipeline-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px; background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; box-shadow: var(--shadow-md); text-align: center; }
  .do-pipeline-empty-btn { display: inline-flex; align-items: center; gap: 6px; padding: 12px 24px; border-radius: 12px; background: linear-gradient(135deg, #4F46E5, #8B5CF6); color: white; font-size: 14px; font-weight: 700; border: none; cursor: pointer; box-shadow: var(--shadow-brand); transition: opacity 0.2s; }
  .do-pipeline-empty-btn:hover { opacity: 0.9; }
  .do-project-row { display: flex; align-items: center; gap: 14px; padding: 14px 0; border-bottom: 1px solid var(--border-light); cursor: pointer; transition: all 0.15s; }
  .do-project-row:hover { background: var(--bg-canvas); margin: 0 -14px; padding-left: 14px; padding-right: 14px; border-radius: 8px; }
  .do-project-row:last-child { border-bottom: none; }
  .do-project-thumb { width: 48px; height: 48px; border-radius: 10px; object-fit: cover; flex-shrink: 0; background: var(--bg-canvas); }
  .do-project-info { flex: 1; min-width: 0; }
  .do-project-name { font-size: 13px; font-weight: 700; color: var(--text-main); margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .do-project-meta { font-size: 11px; color: var(--text-muted); margin: 0; }
  .do-status-pill { font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.04em; }
  .do-status-draft { background: #fef3c7; color: #92400e; }
  .do-status-ready { background: #dbeafe; color: #1e40af; }
  .do-status-published { background: #dcfce7; color: #15803d; }
  .do-coach-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 20px; box-shadow: var(--shadow-md); display: flex; flex-direction: column; align-items: center; text-align: center; }
  .do-coach-ring { width: 100px; height: 100px; position: relative; margin-bottom: 14px; }
  .do-coach-ring svg { transform: rotate(-90deg); }
  .do-coach-ring-track { fill: none; stroke: var(--border-light); stroke-width: 6; }
  .do-coach-ring-fill { fill: none; stroke: url(#coachGrad); stroke-width: 6; stroke-linecap: round; transition: stroke-dashoffset 1s ease; }
  .do-coach-ring-text { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-family: var(--font-display); font-size: 24px; font-weight: 700; color: #4F46E5; }
  .do-coach-title { font-size: 14px; font-weight: 700; color: var(--text-main); margin: 0 0 4px; }
  .do-coach-sub { font-size: 12px; color: var(--text-muted); margin: 0 0 16px; line-height: 1.5; }
  .do-coach-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 10px; background: linear-gradient(135deg, #4F46E5, #8B5CF6); color: white; font-size: 12px; font-weight: 700; border: none; cursor: pointer; box-shadow: var(--shadow-soft); transition: opacity 0.2s; text-decoration: none; }
  .do-coach-btn:hover { opacity: 0.9; }
  .do-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; }
  .do-modal { background: var(--bg-surface); width: 100%; max-width: 480px; border-radius: 20px; padding: 28px; box-shadow: var(--shadow-xl); border: 1px solid var(--border-light); }
  .do-modal-title { font-family: var(--font-display); font-size: 20px; font-weight: 700; color: var(--text-main); margin: 0 0 6px; }
  .do-modal-sub { font-size: 13px; color: var(--text-muted); margin: 0 0 24px; }
  .do-creation-option { display: flex; align-items: center; gap: 16px; padding: 16px; border-radius: 14px; border: 2px solid var(--border-light); background: var(--bg-canvas); cursor: pointer; transition: all 0.2s; width: 100%; text-align: left; margin-bottom: 10px; }
  .do-creation-option:last-child { margin-bottom: 0; }
  .do-creation-option:hover { border-color: var(--brand-primary); background: var(--brand-secondary); }
  .do-creation-option-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .do-creation-label { font-size: 14px; font-weight: 700; color: var(--text-main); margin: 0 0 2px; }
  .do-creation-desc { font-size: 12px; color: var(--text-muted); margin: 0; }
  .do-scan-banner { display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-radius: 14px; }
  .do-scan-banner-text { flex: 1; }
  .do-scan-banner-title { font-size: 13px; font-weight: 700; color: var(--text-main); margin: 0 0 2px; }
  .do-scan-banner-sub { font-size: 12px; color: var(--text-muted); margin: 0; }
  .do-scan-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 9px; background: var(--brand-primary); color: var(--brand-primary-contrast, white); font-size: 12px; font-weight: 700; border: none; cursor: pointer; white-space: nowrap; transition: opacity 0.2s; }
  .do-scan-btn:hover { opacity: 0.9; }
  .do-scan-dismiss { background: none; border: none; cursor: pointer; color: var(--text-muted); padding: 4px; border-radius: 6px; display: flex; align-items: center; }
  .do-scan-dismiss:hover { background: var(--border-light); }
  .do-scanresult-tag { display: inline-block; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 8px; background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); margin: 0 6px 6px 0; }
  @media (max-width: 1024px) { .do-stat-grid { grid-template-columns: repeat(2,1fr); } .do-actions-grid { grid-template-columns: repeat(2,1fr); } .do-two-col { grid-template-columns: 1fr !important; } .do-pipeline { flex-wrap: wrap; gap: 8px; } .do-pipeline-connector { display: none; } }
  @media (max-width: 640px) { .do-stat-grid { grid-template-columns: 1fr 1fr; } }
`;

function getRelativeTime(dateString) {
  if (!dateString) return 'Recently';
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 60) return `${m || 1}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

function StatCard({ label, value, delta, up, icon: Icon, loading }) {
  return (
    <div className="do-stat-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <p className="do-stat-label">{label}</p>
        <Icon size={14} color="var(--text-light)" />
      </div>
      {loading
        ? <div className="do-skeleton" style={{ height: 28, width: 72, marginBottom: 8 }} />
        : <p className="do-stat-value">{value}</p>
      }
      {delta && !loading && (
        <span className={`do-delta ${up ? 'do-delta-up' : 'do-delta-down'}`}>
          {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {delta}
        </span>
      )}
    </div>
  );
}

function CircularProgress({ value, size = 100, stroke = 6 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className="do-coach-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <defs>
          <linearGradient id="coachGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4F46E5" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
        <circle className="do-coach-ring-track" cx={size/2} cy={size/2} r={radius} />
        <circle className="do-coach-ring-fill" cx={size/2} cy={size/2} r={radius}
          strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <span className="do-coach-ring-text">{value}%</span>
    </div>
  );
}

export default function DashboardOverview() {
  const { currentUser, userData, refreshUserData } = useAuth();

  const { summaryData: coachSummary, isLoading: coachLoading } = useAnalytics({ days: 7, platform: 'all' });
  const engagementStat = coachSummary?.summary?.engagement;
  const hasRealEngagement = !!engagementStat?.value && coachSummary?.isDemo !== true;
  const { addToast } = useToast();
  const navigate = useNavigate();
  const uid = currentUser?.uid;

  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScanBanner, setShowScanBanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    const igConnected = userData?.connectedAPIs?.instagram?.connected;
    const hasColors   = userData?.brandColors?.length > 0;
    const dismissed   = sessionStorage.getItem('scan_banner_dismissed');
    if (igConnected && !hasColors && !dismissed) setShowScanBanner(true);
  }, [userData]);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 700);
    return () => clearTimeout(t);
  }, []);

  const { data: videosData } = useQuery({
    queryKey: ['dashboardVideos', uid],
    enabled: !!uid,
    queryFn: () => api.get('/video/my-videos'),
    staleTime: 60000,
  });

  const { data: captionsData } = useQuery({
    queryKey: ['dashboardCaptions', uid],
    enabled: !!uid,
    queryFn: () => api.get('/captions?limit=50'),
    staleTime: 60000,
  });

  const bannerStyle = useMemo(() => {
    const colors = userData?.brandColors;
    if (Array.isArray(colors) && colors.length >= 2) {
      return {
        background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
        color: getContrastText(colors[0]),
      };
    }
    return { background: 'linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)', color: '#FFFFFF' };
  }, [userData?.brandColors]);

  const [serviceStatus, setServiceStatus] = useState({});

  useEffect(() => {
    api.get('/social/service-status').then(res => {
      if (res.success) setServiceStatus(res.services || {});
    }).catch(() => {});
  }, []);

  const platformStatus = useMemo(() => {
    return Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
      const conn = userData?.connectedAPIs?.[key];
      const svc = serviceStatus[key] || DEFAULT_SERVICE_STATUS[key] || { mode: 'coming-soon', label: 'Coming Soon', available: false };
      const isConnected = !!(conn?.connected);
      const canPublish = isConnected && svc.available && svc.mode !== 'coming-soon';
      return {
        id: key, ...config,
        connected: isConnected,
        username: conn?.username || conn?.displayName || null,
        followers: conn?.followerCount || conn?.followersCount || 0,
        serviceStatus: svc.mode,
        serviceLabel: svc.label,
        canPublish,
        blockedReason: isConnected && !canPublish
          ? (svc.mode === 'coming-soon'
              ? 'Publishing coming soon — connect now to be ready at launch'
              : svc.mode === 'sandbox'
              ? 'Sandbox mode — limited to test accounts'
              : 'Service temporarily unavailable')
          : null
      };
    });
  }, [userData, serviceStatus]);

  const activityItems = useMemo(() => {
    const videos = videosData?.videos || [];
    if (videos.length === 0) return [
      { id: 'demo-1', action: 'Upload your first video', detail: 'Head to Video Tools to get started', time: 'Now', color: '#4F46E5' },
      { id: 'demo-2', action: 'Connect your platforms', detail: 'Instagram and TikTok ready to link', time: 'Now', color: '#E1306C' },
    ];
    return videos.slice(0, 5).map(v => ({
      id: v._id, action: 'Video Uploaded',
      detail: `${v.name || 'Untitled'} • ${v.status}`,
      time: getRelativeTime(v.createdAt), color: '#F59E0B',
    }));
  }, [videosData]);

  const postHistory = userData?.postHistory || [];
  const captions = captionsData?.captions || [];

  const pipeline = useMemo(() => {
    const videos = videosData?.videos || [];
    if (videos.length === 0) return null;

    const sorted = [...videos].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const latest = sorted[0];

    const hasUpload  = true;
    const hasEnhance = !!(latest.processedUrl || latest.captionedVideoUrl);
    const hasGenerate = captions.some(c => c.videoId === latest._id || c.videoId === String(latest._id));
    const hasPublish = postHistory.some(p => {
      const pid = p.videoId?._id || p.videoId;
      return String(pid) === String(latest._id) && p.publishStatus === 'published';
    });
    const hasPreview = hasGenerate || hasPublish;

    const steps = [
      { id: 'upload',   label: 'Upload',   done: hasUpload,   verified: true,  path: '/dashboard/edit-studio',  desc: 'Add your content' },
      { id: 'enhance',  label: 'Enhance',  done: hasEnhance,  verified: true,  path: '/dashboard/edit-studio',  desc: 'AI optimization' },
      { id: 'generate', label: 'Generate', done: hasGenerate, verified: true,  path: '/dashboard/edit-studio',  desc: 'Captions & hooks' },
      { id: 'preview',  label: 'Preview',  done: hasPreview,  verified: true,  path: '/dashboard/preview',      desc: 'Multi-platform' },
      { id: 'publish',  label: 'Publish',  done: hasPublish,  verified: true,  path: '/dashboard/calendar',     desc: 'Go live' },
    ];

    const firstIncompleteVerified = steps.findIndex(s => s.verified && !s.done);
    const currentIdx = firstIncompleteVerified === -1 ? steps.length - 1 : firstIncompleteVerified;
    const isFullyComplete = firstIncompleteVerified === -1;

    return { steps, currentIdx, latest, isFullyComplete };
  }, [videosData, userData, captionsData]);

  const plan = userData?.subscriptionPlan || 'freemium';
  const planConfig = PLAN_CONFIG[plan] || PLAN_CONFIG.freemium;

  const creditsUsed  = userData?.monthlyUsage?.monthlyGenerativeCreditsUsed || 0;
  const creditsTotal = userData?.planLimits?.monthlyGenerativeCredits || 100;
  const creditsPct   = Math.min((creditsUsed / creditsTotal) * 100, 100);

  const stats = [
    {
      label: 'Total Views',
      value: hasRealEngagement ? (coachSummary.summary.views?.value ?? '0') : '—',
      delta: hasRealEngagement ? coachSummary.summary.views?.delta : null,
      up: coachSummary?.summary?.views?.up ?? true,
      icon: Eye,
    },
    {
      label: 'Engagement',
      value: hasRealEngagement ? engagementStat.value : '—',
      delta: hasRealEngagement ? engagementStat.delta : null,
      up: engagementStat?.up ?? true,
      icon: Heart,
    },
    { label: 'Followers',        value: platformStatus.find(p=>p.connected)?.followers?.toLocaleString() || '—', delta: null, up: true, icon: Users },
    { label: 'Posts This Month', value: String(userData?.monthlyUsage?.postsGenerated || 0), delta: null, up: true, icon: Target },
  ];

  const recentProjects = useMemo(() => {
    const videos = videosData?.videos || [];
    if (videos.length === 0) return [];
    return videos.slice(0, 5).map(v => {
      const isPublished = postHistory.some(p => {
        const pid = p.videoId?._id || p.videoId;
        return String(pid) === String(v._id) && p.publishStatus === 'published';
      });
      const hasCaps = captions.some(c => c.videoId === v._id || c.videoId === String(v._id));
      const status = isPublished ? 'Published' : hasCaps ? 'Ready' : v.processedUrl ? 'Enhanced' : 'Draft';
      const rawUrl = v.originalVideo?.secure_url;
      const thumb = v.thumbnailUrl || (rawUrl
        ? rawUrl
            .replace('/upload/', '/upload/so_0,w_100,h_100,c_thumb/')
            .replace(/\.(mp4|mov|webm|avi)(\?.*)?$/i, '.jpg')
        : null);
      return {
        id: v._id,
        name: v.name || 'Untitled',
        platforms: ['Instagram', 'TikTok'],
        status,
        thumb: thumb || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=100&h=100&fit=crop',
        updated: getRelativeTime(v.updatedAt || v.createdAt),
        video: v,
      };
    });
  }, [videosData, captionsData, userData]);

  // FIX: this called /social/instagram/refresh-profile — a basic profile
  // info refresh (username, follower count), NOT the actual brand scan
  // service. The real AI-powered analysis (brandScanService.js) was fully
  // built and never triggered by this button — the root cause of "brand
  // scan doesn't surface anything." Now calls the real scan, applies the
  // result to the profile, and surfaces a genuine visible result instead
  // of a generic toast.
  const handleBrandScan = async () => {
    setScanning(true);
    try {
      const igUsername = userData?.connectedAPIs?.instagram?.username;
      if (!igUsername) {
        throw new Error('Connect Instagram first so we know which profile to scan.');
      }
      const profileUrl = `https://instagram.com/${igUsername}`;

      const result = await api.post('/brand-scan/scan', { url: profileUrl, depth: 'standard' });

      if (!result.success) {
        throw new Error(result.error || 'Brand scan failed');
      }

      await api.post('/brand-scan/apply', { scanResult: result });

      if (typeof refreshUserData === 'function') await refreshUserData();

      setScanResult(result);
      setShowScanBanner(false);
      sessionStorage.setItem('scan_banner_dismissed', 'true');
      addToast({ type: 'success', message: "Got it — I know your vibe now." });
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Brand scan failed. Check your Instagram connection.' });
    }
    setScanning(false);
  };

  const handleDismissScan = () => {
    setShowScanBanner(false);
    sessionStorage.setItem('scan_banner_dismissed', 'true');
  };

  const handlePipelineClick = (step, idx) => {
    const config = PIPELINE_STEPS.find(s => s.id === step.id);
    if (!config) return;
    const asset = pipeline?.latest;
    navigate(config.path, { state: { video: asset, preloadedMedia: asset } });
  };

  const handleCreationChoice = (path) => {
    setShowCreateModal(false);
    navigate(path);
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="do-root">
      <style>{DO_CSS}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 3px', letterSpacing: '-0.02em' }}>
            {greeting()}, {userData?.brandName || userData?.displayName || 'Creator'} 👋
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Let's make something worth posting today.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary-contrast"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', boxShadow: 'var(--shadow-brand)' }}
          >
            <Plus size={14} /> New Project
          </button>
          {/* FIX: this duplicated the bell and avatar DashboardLayout.js
              already renders persistently in its own topbar, right above
              this page's content — two bells, two avatars stacked on top
              of each other. This one's bell didn't even have a click
              handler. Removed; DashboardLayout's is now the single point
              of contact for notifications, feedback, and profile. */}
        </div>
      </div>

      {/* FIX: an expired Instagram token previously failed silently on every
          data fetch — analytics, brand scan, everything degraded quietly to
          defaults with zero indication why. This is the real, visible signal
          that was missing, matching the same banner pattern already used
          for brand scan below. */}
      {userData?.connectedAPIs?.instagram?.needsReconnect && (
        <div className="do-scan-banner" style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Scan size={20} color="#92400E" />
          </div>
          <div className="do-scan-banner-text">
            <p className="do-scan-banner-title">Your Instagram connection needs a refresh</p>
            <p className="do-scan-banner-sub">Analytics and brand scan can't pull real data until you reconnect — takes 10 seconds.</p>
          </div>
          <button className="do-scan-btn" onClick={() => navigate('/dashboard/profile')} style={{ background: '#92400E', color: '#FFFFFF' }}>
            Reconnect
          </button>
        </div>
      )}

      {showScanBanner && (
        <div className="do-scan-banner" style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #EEF2FF 100%)', border: '1px solid #C7D2FE' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--brand-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Scan size={20} color="#4F46E5" />
          </div>
          <div className="do-scan-banner-text">
            <p className="do-scan-banner-title">Let's get to know your vibe</p>
            <p className="do-scan-banner-sub">We'll read your last few posts to learn how you actually sound — no forms, no guessing.</p>
          </div>
          <button className="do-scan-btn" onClick={handleBrandScan} disabled={scanning} style={{ background: '#4F46E5', color: '#FFFFFF' }}>
            {scanning ? <Loader2 size={13} className="do-spin" /> : <Sparkles size={13} />}
            {scanning ? 'Reading your vibe...' : 'Sync My Vibe'}
          </button>
          <button className="do-scan-dismiss" onClick={handleDismissScan}><X size={16} /></button>
        </div>
      )}

      {scanResult && (
        <div className="do-modal-overlay" onClick={() => setScanResult(null)}>
          <div className="do-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h2 className="do-modal-title">Here's what we learned about your brand</h2>
                <p className="do-modal-sub">This now shapes how Infleuince writes for you.</p>
              </div>
              <button onClick={() => setScanResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={20} />
              </button>
            </div>
            {scanResult.tokenExpired ? (
              <div style={{ padding: '14px 16px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 12, marginBottom: 4 }}>
                <p style={{ fontSize: 13, color: '#92400E', margin: 0, lineHeight: 1.5 }}>
                  Your Instagram connection expired, so this is a generic starting point, not a real read of your content. Reconnect Instagram and run the scan again for your actual voice and pillars.
                </p>
              </div>
            ) : (
              <>
                {scanResult.brandSummary && (
                  <p style={{ fontSize: 14, color: 'var(--text-main)', lineHeight: 1.6, marginBottom: 18 }}>{scanResult.brandSummary}</p>
                )}
                {scanResult.brandTone?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Your Voice</p>
                    {scanResult.brandTone.map(t => <span key={t} className="do-scanresult-tag">{t}</span>)}
                  </div>
                )}
                {scanResult.contentPillars?.length > 0 && (
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Content Pillars</p>
                    {scanResult.contentPillars.map(p => <span key={p} className="do-scanresult-tag">{p}</span>)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="do-stat-grid">
        {stats.map((s, i) => <StatCard key={i} {...s} loading={loading} />)}
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <p className="do-section-title" style={{ margin: 0 }}>What's Next</p>
          <a href="/dashboard/edit-studio" style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', textDecoration: 'none' }}>See all →</a>
        </div>
        {pipeline ? (
          <>
            <div className="do-pipeline">
              {PIPELINE_STEPS.map((step, idx) => {
                const state = pipeline.steps[idx];
                const isDone = state?.done;
                const isCurrent = idx === pipeline.currentIdx && !isDone;
                const StepIcon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    <div
                      className={`do-pipeline-step ${isDone ? 'done' : isCurrent ? 'current' : 'muted'}`}
                      onClick={() => !isDone && handlePipelineClick(state, idx)}
                      style={{ cursor: isDone ? 'default' : 'pointer' }}
                    >
                      <div className="do-pipeline-icon-wrap">
                        {isDone ? <CheckCircle2 size={18} /> : <StepIcon size={18} className="do-pipeline-icon" />}
                      </div>
                      <span className="do-pipeline-label">{step.label}</span>
                      <p className="do-pipeline-desc">{step.desc}</p>
                      {isCurrent && (
                        <span className="do-pipeline-cta" style={{ background: '#4F46E5', color: '#FFFFFF' }}>
                          Continue here <ArrowRight size={10} />
                        </span>
                      )}
                    </div>
                    {idx < PIPELINE_STEPS.length - 1 && (
                      <div className={`do-pipeline-connector ${isDone ? 'done' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {pipeline.isFullyComplete && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, padding: '10px 16px', background: 'var(--brand-secondary)', border: '1px solid var(--brand-border)', borderRadius: 12, fontSize: 12, color: 'var(--brand-secondary-contrast, var(--brand-primary))' }}>
                <span>🎉 This video's pipeline is complete — nothing left to continue here.</span>
                <button
                  onClick={() => setShowCreateModal(true)}
                  style={{ background: 'var(--brand-primary)', color: 'var(--brand-primary-contrast, white)', border: 'none', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 12 }}
                >
                  Start New Video
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="do-pipeline-empty">
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--brand-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Upload size={24} color="#4F46E5" />
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' }}>Drop your first video in</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Your content pipeline will track your progress from upload to publish.</p>
            </div>
            <button className="do-pipeline-empty-btn btn-primary-contrast" onClick={() => navigate('/dashboard/edit-studio')}>
              <Upload size={16} /> Upload Video
            </button>
          </div>
        )}
      </div>

      <div className="do-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>

        <div className="do-card">
          <p className="do-section-title">Recent Projects</p>
          {recentProjects.length > 0 ? recentProjects.map(p => (
            <div key={p.id} className="do-project-row" onClick={() => navigate('/dashboard/preview', { state: { video: p.video } })}>
              <img src={p.thumb} alt="" className="do-project-thumb" />
              <div className="do-project-info">
                <p className="do-project-name">{p.name}</p>
                <p className="do-project-meta">{p.platforms.join(' • ')} • Updated {p.updated}</p>
              </div>
              <span className={`do-status-pill do-status-${p.status.toLowerCase()}`}>{p.status}</span>
            </div>
          )) : (
            <div style={{ textAlign: 'center', padding: '32px 20px' }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>Nothing here yet — drop your first video in and let's go.</p>
              <button onClick={() => navigate('/dashboard/edit-studio')} className="btn-primary-contrast" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                <Upload size={14} /> Upload Video
              </button>
            </div>
          )}
        </div>

        <div className="do-coach-card">
          {coachLoading ? (
            <>
              <div className="do-skeleton" style={{ width: 100, height: 100, borderRadius: '50%', margin: '0 auto 12px' }} />
              <div className="do-skeleton" style={{ height: 16, width: '70%', margin: '0 auto 8px' }} />
            </>
          ) : hasRealEngagement ? (
            <>
              <CircularProgress value={Math.min(Math.abs(parseFloat(engagementStat.value)) || 0, 100)} />
              <p className="do-coach-title">
                {engagementStat.up !== false ? 'Your engagement is up ' : 'Your engagement is down '}
                {engagementStat.value}!
              </p>
              <p className="do-coach-sub">
                {engagementStat.delta ? `${engagementStat.delta} vs. last period.` : ''} Keep publishing to sharpen these insights.
              </p>
            </>
          ) : (
            <>
              <CircularProgress value={0} />
              <p className="do-coach-title">Still warming up</p>
              <p className="do-coach-sub">Post a few times and I'll start telling you what's actually working — not just numbers.</p>
            </>
          )}
          <a href="/dashboard/analytics" className="do-coach-btn btn-primary-contrast">
            See what's working <ChevronRight size={14} />
          </a>
        </div>
      </div>

      <div className="do-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        <div className="do-card">
          <p className="do-section-title">Quick Actions</p>
          <div className="do-actions-grid">
            {QUICK_ACTIONS.map((a, i) => (
              <button key={i} className="do-action-btn" onClick={() => navigate(a.path)}>
                <div className="do-action-icon" style={{ background: `${a.color}15` }}>
                  <a.icon size={17} color={a.color} />
                </div>
                <span className="do-action-label">{a.label}</span>
              </button>
            ))}
          </div>

          <p className="do-section-title" style={{ marginTop: 22 }}>Recent Activity</p>
          {activityItems.map(item => (
            <div key={item.id} className="do-activity-row">
              <div className="do-activity-dot" style={{ background: item.color }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 2px' }}>{item.action}</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 2px' }}>{item.detail}</p>
                <p style={{ fontSize: 10, color: 'var(--text-light)', margin: 0 }}>{item.time}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="do-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <p className="do-section-title" style={{ margin: 0 }}>Connected Platforms</p>
            <button onClick={() => navigate('/dashboard/profile')} style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-primary)', background: 'none', border: 'none', cursor: 'pointer' }}>Manage →</button>
          </div>

          {platformStatus.map(p => {
            const Icon = p.icon;
            return (
              <div key={p.id} className="do-platform-row">
                <div style={{ width: 34, height: 34, borderRadius: 9, background: `${p.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={15} color={p.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 1px' }}>{p.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                    {p.connected ? `@${p.username || 'connected'} • ${(p.followers || 0).toLocaleString()} followers` : 'Not connected'}
                  </p>
                </div>
                {p.connected ? (
                  p.canPublish ? (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 0 3px #10B98120', flexShrink: 0 }} />
                  ) : (
                    <span className={`status-pill ${p.serviceStatus === 'sandbox' ? 'sandbox' : p.serviceStatus === 'coming-soon' ? 'coming-soon' : 'pending'}`}>
                      {p.serviceLabel}
                    </span>
                  )
                ) : (
                  <button className="do-connect-btn" onClick={() => navigate(`/dashboard/profile?connect=${p.id}`)}>Connect</button>
                )}
              </div>
            );
          })}

          <div className="contrast-bar" style={{ marginTop: 16, padding: '14px 16px', borderRadius: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)' }}>AI Credits</span>
              <span
                style={{
                  fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                  padding: '2px 8px', borderRadius: 999, color: '#FFFFFF', background: planConfig.color,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}
              >
                {planConfig.label}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{creditsUsed} / {creditsTotal} used</span>
            </div>
            <div style={{ height: 5, background: 'var(--border-light)', borderRadius: 9999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${creditsPct}%`, background: 'linear-gradient(90deg, var(--brand-primary), var(--brand-accent))', borderRadius: 9999, transition: 'width 0.5s ease' }} />
            </div>
            <p style={{ fontSize: 11, color: '#4B5563', margin: '6px 0 0' }}>
              Resets monthly.{plan !== 'executive' && (
                <> <button onClick={() => navigate('/dashboard/pricing')} style={{ color: '#4F46E5', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11 }}>Upgrade for more</button></>
              )}
            </p>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="do-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="do-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 className="do-modal-title">What are you creating?</h2>
                <p className="do-modal-sub">Choose a format and we&apos;ll take you to the right workflow.</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={20} />
              </button>
            </div>
            {CREATION_OPTIONS.map(opt => (
              <button key={opt.id} className="do-creation-option" onClick={() => handleCreationChoice(opt.path)}>
                <div className="do-creation-option-icon" style={{ background: `${opt.color}15` }}>
                  <opt.icon size={22} color={opt.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <p className="do-creation-label">{opt.label}</p>
                  <p className="do-creation-desc">{opt.desc}</p>
                </div>
                <ChevronRight size={16} color="var(--text-light)" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}